import { getAuthHeader } from "../auth/AuthContext";

const BASE = "/api";

const req = async (method, path, body) => {
  const res = await fetch(BASE + path, {
    method,
    headers: {
      ...(await getAuthHeader()),
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  if (res.status === 204) return null;
  return res.json();
};

export const api = {
  // Webhooks
  getWebhooks: () => req("GET", "/webhooks"),
  createWebhook: (d) => req("POST", "/webhooks", d),
  deleteWebhook: (id) => req("DELETE", `/webhooks/${id}`),
  getWebhookDeliveries: (id) => req("GET", `/webhooks/${id}/deliveries`),

  // Amtsperioden
  getAmtsperioden: () => req("GET", "/amtsperioden"),
  createAmtsperiode: (d) => req("POST", "/amtsperioden", d),

  // Sitzungsgeld
  getSitzungsgeldregeln: () => req("GET", "/sitzungsgeldregeln"),
  createSitzungsgeldregel: (d) => req("POST", "/sitzungsgeldregeln", d),
  sitzungsgeldBerechnen: (sitzungId) => req("POST", `/sitzungen/${sitzungId}/sitzungsgeld/berechnen`),
  getSitzungsgeld: (sitzungId) => req("GET", `/sitzungen/${sitzungId}/sitzungsgeld`),
  sitzungsgeldStatus: (id, status) => req("POST", `/sitzungsgeldberechnungen/${id}/status`, { status }),

  // Bürgerportal-Zugangsschutz
  getBuergerportalStatus: () => req("GET", "/buergerportal-einstellungen/status"),
  buergerportalPasswortSetzen: (passwort) => req("PUT", "/buergerportal-einstellungen/passwort", { passwort }),
  buergerportalPasswortEntfernen: () => req("DELETE", "/buergerportal-einstellungen/passwort"),

  // API-Schlüssel (externe Integrationen)
  getApiKeys: () => req("GET", "/api-keys"),
  createApiKey: (bezeichnung, scope) => req("POST", "/api-keys", { bezeichnung, scope }),
  revokeApiKey: (id) => req("DELETE", `/api-keys/${id}`),

  // Suche
  suche: (q) => req("GET", `/suche?q=${encodeURIComponent(q)}`),

  // Notifications
  getNotifications: (nurUngelesen) => req("GET", `/notifications${nurUngelesen ? "?nur_ungelesen=true" : ""}`),
  getAnzahlUngelesen: () => req("GET", "/notifications/anzahl-ungelesen"),
  notificationGelesen: (id) => req("POST", `/notifications/${id}/gelesen`),
  alleNotificationsGelesen: () => req("POST", "/notifications/alle-gelesen"),

  // Persönliche Notizen
  getNotizen: (objektTyp, objektId) => req("GET", `/notizen/${objektTyp}/${objektId}`),
  createNotiz: (objektTyp, objektId, text) => req("POST", `/notizen/${objektTyp}/${objektId}`, { text }),
  updateNotiz: (id, text) => req("PUT", `/notizen/${id}`, { text }),
  deleteNotiz: (id) => req("DELETE", `/notizen/${id}`),

  // Veröffentlichung (Phase 9) - Verknüpfung zum Bürgerportal
  getVeroeffentlichung: (typ, id) => req("GET", `/veroeffentlichung/${typ}/${id}`),
  veroeffentlichungFreigeben: (typ, id) => req("POST", `/veroeffentlichung/${typ}/${id}/freigeben`),
  veroeffentlichen: (typ, id, geplant_fuer) => req("POST", `/veroeffentlichung/${typ}/${id}/veroeffentlichen`, geplant_fuer ? { geplant_fuer } : {}),
  veroeffentlichungZurueckziehen: (typ, id) => req("POST", `/veroeffentlichung/${typ}/${id}/zurueckziehen`),

  // Vorlagen
  getVorlagen: (status) => req("GET", `/vorlagen${status ? `?status=${status}` : ""}`),
  getVorlage: (id) => req("GET", `/vorlagen/${id}`),
  createVorlage: (d) => req("POST", "/vorlagen", d),
  updateVorlage: (id, d) => req("PUT", `/vorlagen/${id}`, d),
  vorlageFreigeben: (id) => req("POST", `/vorlagen/${id}/freigeben`),
  vorlageZurueckziehen: (id) => req("POST", `/vorlagen/${id}/zurueckziehen`),
  deleteVorlage: (id) => req("DELETE", `/vorlagen/${id}`),

  // Workflow
  getWorkflowStatus: (typ, id) => req("GET", `/workflow/${typ}/${id}`),
  workflowWeiterleiten: (instId, kommentar) => req("POST", `/workflow-instanzen/${instId}/weiterleiten`, { kommentar }),
  workflowAblehnen: (instId, kommentar) => req("POST", `/workflow-instanzen/${instId}/ablehnen`, { kommentar }),

  // Beratungsfolge
  getBeratungsfolge: (vid) => req("GET", `/vorlagen/${vid}/beratungsfolge`),
  addBeratungsschritt: (vid, d) => req("POST", `/vorlagen/${vid}/beratungsfolge`, d),

  // Vorlagen-Anlagen
  getVorlageAnlagen: (vid) => req("GET", `/vorlagen/${vid}/anlagen`),
  vorlageAnlageUrl: (aid) => `/api/vorlagen-anlagen/${aid}/datei.pdf`,
  deleteVorlageAnlage: (aid) => req("DELETE", `/vorlagen-anlagen/${aid}`),
  uploadVorlageAnlage: (vid, file) => {
    const fd = new FormData();
    fd.append("datei", file);
    return fetch(`/api/vorlagen/${vid}/anlagen`, {
      method: "POST", body: fd,
      headers: { Authorization: "Bearer " + (localStorage.getItem("internal_token") || "") },
    }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
  },
  updateBeratungsschritt: (vid, bid, d) => req("PUT", `/vorlagen/${vid}/beratungsfolge/${bid}`, d),

  // Mitzeichnung
  getMeineMitzeichnungen: () => req("GET", "/meine-mitzeichnungen"),
  mitzeichnungEntscheiden: (vid, mid, entscheidung, kommentar) =>
    req("POST", `/vorlagen/${vid}/mitzeichnung/${mid}/entscheiden`, { entscheidung, kommentar }),

  // Beschlusskontrolle
  getBeschlusskontrolleDashboard: () => req("GET", "/beschlusskontrolle/dashboard"),

  // Gremienportal
  getMeinDashboard: () => req("GET", "/mein-dashboard"),

  // Keycloak
  getKeycloakGroups: () => req("GET", "/keycloak/groups"),
  getKeycloakUsers: () => req("GET", "/keycloak/users"),

  // Mitglieder
  getMitglieder: () => req("GET", "/mitglieder"),
  createMitglied: (d) => req("POST", "/mitglieder", d),
  updateMitglied: (id, d) => req("PUT", `/mitglieder/${id}`, d),
  deleteMitglied: (id) => req("DELETE", `/mitglieder/${id}`),

  // Gremien
  getGremien: (withPerm) => req("GET", "/gremien" + (withPerm ? `?with_perm=${withPerm}` : "")),
  getGremium: (id) => req("GET", `/gremien/${id}`),
  createGremium: (d) => req("POST", "/gremien", d),
  updateGremium: (id, d) => req("PUT", `/gremien/${id}`, d),
  addMitglied: (gid, mid) => req("POST", `/gremien/${gid}/mitglieder/${mid}`),
  removeMitglied: (gid, mid) => req("DELETE", `/gremien/${gid}/mitglieder/${mid}`),

  // Sitzungen
  getSitzungen: () => req("GET", "/sitzungen"),
  getSitzung: (id) => req("GET", `/sitzungen/${id}`),
  createSitzung: (d) => req("POST", "/sitzungen", d),
  updateSitzung: (id, d) => req("PUT", `/sitzungen/${id}`, d),
  deleteSitzung: (id) => req("DELETE", `/sitzungen/${id}`),
  abschliessenSitzung: (id) => req("POST", `/sitzungen/${id}/abschliessen`),
  absagenSitzung: (id, d) => req("POST", `/sitzungen/${id}/absagen`, d),

  // TOPs
  createTOP: (sid, d) => req("POST", `/sitzungen/${sid}/tops`, d),
  updateTOP: (sid, tid, d) => req("PUT", `/sitzungen/${sid}/tops/${tid}`, d),
  deleteTOP: (sid, tid) => req("DELETE", `/sitzungen/${sid}/tops/${tid}`),
  setAbstimmung: (sid, tid, d) => req("POST", `/sitzungen/${sid}/tops/${tid}/abstimmung`, d),

  // Ladung
  ladungPdfUrl: (id) => `${BASE}/sitzungen/${id}/ladung/pdf`,
  ladungPersonalPdfUrl: (id) => '/api/sitzungen/' + id + '/ladung/personal-pdf',
  seriendruck_url: (id) => `${BASE}/sitzungen/${id}/ladung/seriendruck`,
  getLadungEmpfaenger: (id) => req("GET", `/sitzungen/${id}/ladung/empfaenger`),
  sendLadung: (id, d) => req("POST", `/sitzungen/${id}/ladung/senden`, d),

  // Protokoll
  getProtokoll: (id) => req("GET", `/sitzungen/${id}/protokoll`),
  addProtokoll: (id, d) => req("POST", `/sitzungen/${id}/protokoll`, d),
  updateProtokoll: (sid, pid, d) => req("PUT", `/sitzungen/${sid}/protokoll/${pid}`, d),
  deleteProtokoll: (sid, pid) => req("DELETE", `/sitzungen/${sid}/protokoll/${pid}`),
  protokollPdfUrl: (id) => `${BASE}/sitzungen/${id}/protokoll/pdf`,

  // Gäste & Anwesenheit
  getGaeste: (sid) => req("GET", `/sitzungen/${sid}/gaeste`),
  createGast: (sid, d) => req("POST", `/sitzungen/${sid}/gaeste`, d),
  updateGast: (sid, gid, d) => req("PUT", `/sitzungen/${sid}/gaeste/${gid}`, d),
  deleteGast: (sid, gid) => req("DELETE", `/sitzungen/${sid}/gaeste/${gid}`),
  getMitgliederListe: (sid) => req("GET", `/sitzungen/${sid}/mitglieder-liste`),
  getAnwesenheit: (sid) => req("GET", `/sitzungen/${sid}/anwesenheit`),
  setAnwesenheit: (sid, d) => req("POST", `/sitzungen/${sid}/anwesenheit`, d),

  // Anträge
  getAntraege: (sid) => req("GET", `/sitzungen/${sid}/antraege`),
  createAntrag: (sid, d) => req("POST", `/sitzungen/${sid}/antraege`, d),
  updateAntrag: (sid, aid, d) => req("PUT", `/sitzungen/${sid}/antraege/${aid}`, d),
  deleteAntrag: (sid, aid) => req("DELETE", `/sitzungen/${sid}/antraege/${aid}`),
  entscheidungAntrag: (sid, aid, d) => req("POST", `/sitzungen/${sid}/antraege/${aid}/entscheidung`, d),
  getMeineAntraege: () => req("GET", "/meine-antraege"),

  // Protokoll Mail
  sendProtokoll: (id) => req("POST", `/sitzungen/${id}/protokoll/senden`),

  // Sitzungsorte
  getSitzungsorte: () => req("GET", "/sitzungsorte"),
  createSitzungsort: (d) => req("POST", "/sitzungsorte", d),
  updateSitzungsort: (id, d) => req("PUT", `/sitzungsorte/${id}`, d),
  deleteSitzungsort: (id) => req("DELETE", `/sitzungsorte/${id}`),

  // Projektor (nr = Projektor-Nummer 1-5, Default 1)
  getProjektorListe: (sid, token) => req("GET", `/projektor/${sid}/liste?token=${encodeURIComponent(token || "")}`),
  getProjektorStatus: (sid, nr = 1, token) => req("GET", `/projektor/${sid}/status?nr=${nr}&token=${encodeURIComponent(token || "")}`),
  setProjektorStatus: (sid, d, nr = 1) => req("POST", `/projektor/${sid}/status?nr=${nr}`, d),
  getProjektorDaten: (sid, nr = 1, token) => req("GET", `/projektor/${sid}/daten?nr=${nr}&token=${encodeURIComponent(token || "")}`),
  setProjektorPdf: (sid, d, nr = 1) => req("POST", `/projektor/${sid}/pdf?nr=${nr}`, d),
  getBeamerToken: (sid) => req("GET", `/sitzungen/${sid}/beamer-token`),
  regenerateBeamerToken: (sid) => req("POST", `/sitzungen/${sid}/beamer-token/regenerieren`),

  // DSGVO-Datenexport
  exportEigeneDaten: () => req("GET", "/mein-konto/datenexport"),
  exportMitgliedDaten: (id) => req("GET", `/admin/mitglieder/${id}/datenexport`),

  // MFA (eigenes Konto)
  mfaEinrichten: () => req("POST", "/internal/mfa/einrichten"),
  mfaAktivieren: (code) => req("POST", "/internal/mfa/aktivieren", { code }),
  mfaDeaktivieren: (passwort) => req("POST", "/internal/mfa/deaktivieren", { passwort }),

  // SSO/OIDC-Konfiguration (Admin)
  getSsoConfig: () => req("GET", "/admin/sso"),
  updateSsoConfig: (d) => req("PUT", "/admin/sso", d),
  testSsoConfig: (d) => req("POST", "/admin/sso/test", d),
  deleteSsoConfig: () => req("DELETE", "/admin/sso"),

  // Dokumenten-Bibliothek
  getDokumente: () => req("GET", "/dokumente"),
  deleteDokument: (did) => req("DELETE", `/dokumente/${did}`),
  dokumentDateiUrl: (did) => `/api/dokumente/${did}/datei.pdf`,
  uploadDokument: (file, titel) => {
    const fd = new FormData();
    fd.append("datei", file);
    if (titel) fd.append("titel", titel);
    return fetch("/api/dokumente", {
      method: "POST", body: fd,
      headers: { Authorization: "Bearer " + (localStorage.getItem("internal_token") || "") },
    }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
  },

  // Audit Log
  getAuditLog: (id) => req("GET", `/sitzungen/${id}/audit`),
  getUserGremien: (uid) => req("GET", `/internal/users/${uid}/gremien`),
  addUserGremium: (uid, gid, d) => req("POST", `/internal/users/${uid}/gremien/${gid}`, d),
  removeUserGremium: (uid, gid) => req("DELETE", `/internal/users/${uid}/gremien/${gid}`),
  getInternalUsers: () => req("GET", "/internal/users"),
  createInternalUser: (d) => req("POST", "/internal/users", d),
  updateInternalUser: (uid, d) => req("PUT", `/internal/users/${uid}`, d),
  deleteInternalUser: (uid) => req("DELETE", `/internal/users/${uid}`),
  unlockInternalUser: (uid) => req("POST", `/internal/users/${uid}/entsperren`),

  antragZurueckziehen: (sid, aid, d) => req("POST", `/sitzungen/${sid}/antraege/${aid}/zurueckziehen`, d),
  antragAbsetzen: (sid, aid, d) => req("POST", `/sitzungen/${sid}/antraege/${aid}/absetzen`, d),
  antragVerschieben: (sid, aid, d) => req("POST", `/sitzungen/${sid}/antraege/${aid}/verschieben`, d),
  topVerschieben: (sid, tid, d) => req("POST", `/sitzungen/${sid}/tops/${tid}/verschieben`, d),
  topVerschiebenSmart: (sid, tid, d) => req("POST", `/sitzungen/${sid}/tops/${tid}/verschieben-smart`, d),
  getNaechsteSitzungen: (sid) => req("GET", `/sitzungen/${sid}/naechste-sitzungen`),

  // Dringlichkeit Zulassung
  setZulassung: (sid, aid, d) => req("POST", `/sitzungen/${sid}/antraege/${aid}/zulassung`, d),

  // Protokoll Genehmigung
  getGenehmigungen: (id) => req("GET", `/sitzungen/${id}/protokoll/genehmigungen`),
  genehmigungAnfordern: (id) => req("POST", `/sitzungen/${id}/protokoll/genehmigung-anfordern`),

  // Zusammenfassung
  saveZusammenfassung: (id, text) => req("PUT", `/sitzungen/${id}/zusammenfassung`, { text }),

  // Templates
  getTemplate: (name) => req("GET", `/einstellungen/template/${name}`),

  // Einstellungen
  getEinstellungen: () => req("GET", "/einstellungen"),
  getEinstellungenFlat: () => req("GET", "/einstellungen/flat"),
  updateEinstellungen: (d) => req("PUT", "/einstellungen", d),
  deleteEinstellung: (key) => req("DELETE", `/einstellungen/${key}`),

  // Beschlüsse
  getBeschluesse: (status) => req("GET", `/beschluesse${status ? `?status=${status}` : ""}`),
  createBeschluss: (d) => req("POST", "/beschluesse", d),
  // Pro-Gremium-Matrix
  gremiumMatrix: (gid) => req("GET", `/admin/gremien/${gid}/matrix`),
  gremiumMatrixToggle: (gid, gruppeId, berId, aktiv) => req("PATCH", `/admin/gremien/${gid}/matrix/${gruppeId}/${berId}`, { aktiv }),
  // Phase B: Abstimmungs-Typen
  getAbstimmungsTypen: () => req("GET", "/abstimmungs-typen"),
  getGremiumAbstimmungsTypen: (gid) => req("GET", `/gremien/${gid}/abstimmungs-typen`),
  patchGremiumAbstimmungsTyp: (gid, typId, body) =>
    req("PATCH", `/gremien/${gid}/abstimmungs-typen/${typId}`, body),
  resetGremiumAbstimmungsTyp: (gid, typId) =>
    req("DELETE", `/gremien/${gid}/abstimmungs-typen/${typId}`),
  // Phase B: Beschlussfähigkeit
  getBeschlussfaehigkeit: (sid) => req("GET", `/sitzungen/${sid}/beschlussfaehigkeit`),
  wiederholungEinberufen: (sid, body) => req("POST", `/sitzungen/${sid}/wiederholung-einberufen`, body),
  getNachfolgerSitzung: (sid) => req("GET", `/sitzungen/${sid}/nachfolger`),

  // Phase C: Wahlen
  listWahlen: (sid) => req("GET", `/sitzungen/${sid}/wahlen`),
  getWahl: (wid) => req("GET", `/wahlen/${wid}`),
  createWahl: (sid, body) => req("POST", `/sitzungen/${sid}/wahlen`, body),
  updateWahl: (wid, body) => req("PATCH", `/wahlen/${wid}`, body),
  deleteWahl: (wid) => req("DELETE", `/wahlen/${wid}`),
  addKandidat: (wid, body) => req("POST", `/wahlen/${wid}/kandidaten`, body),
  removeKandidat: (wid, kid) => req("DELETE", `/wahlen/${wid}/kandidaten/${kid}`),
  zurueckziehenKandidat: (wid, kid, grund) =>
    req("POST", `/wahlen/${wid}/kandidaten/${kid}/zurueckziehen?grund=${encodeURIComponent(grund || "")}`),
  eroeffnenWahl: (wid) => req("POST", `/wahlen/${wid}/eroeffnen`),
  schliessenWahl: (wid) => req("POST", `/wahlen/${wid}/schliessen`),
  abbrechenWahl: (wid, grund) =>
    req("POST", `/wahlen/${wid}/abbrechen?grund=${encodeURIComponent(grund || "")}`),
  abgebenStimme: (wid, body) => req("POST", `/wahlen/${wid}/stimme`, body),
  listAnnahmen: (wid) => req("GET", `/wahlen/${wid}/annahmen`),
  updateAnnahme: (aid, body) => req("PATCH", `/annahmen/${aid}`, body),
  gremiumRecomputeDefaults: (gid) => req("POST", `/gremien/${gid}/recompute-defaults`),
  updateBeschluss: (id, d) => req("PUT", `/beschluesse/${id}`, d),
  updateBeschlussFortschritt: (id, fortschritt_prozent) => req("PUT", `/beschluesse/${id}/fortschritt`, { fortschritt_prozent }),
  beschlussvorlagePdfUrl: (sid, aid) => `/api/sitzungen/${sid}/antraege/${aid}/beschlussvorlage.pdf`,
  beschlussPdfUrl: (sid, bid) => `/api/sitzungen/${sid}/beschluesse/${bid}/beschluss.pdf`,
  inBeschlussUmwandeln: (sid, aid, data) => req("POST", `/sitzungen/${sid}/antraege/${aid}/in-beschluss-umwandeln`, data),
  antragBeschlussInfo: (sid, aid) => req("GET", `/sitzungen/${sid}/antraege/${aid}/beschluss-info`),
  listStandardTops: (gid) => req("GET", `/gremien/${gid}/standard-tops`),
  createStandardTop: (gid, data) => req("POST", `/gremien/${gid}/standard-tops`, data),
  updateStandardTop: (gid, tid, data) => req("PUT", `/gremien/${gid}/standard-tops/${tid}`, data),
  deleteStandardTop: (gid, tid) => req("DELETE", `/gremien/${gid}/standard-tops/${tid}`),
  reorderStandardTops: (gid, ids) => req("POST", `/gremien/${gid}/standard-tops/reorder`, { ids }),
  listAnlagen: (tid) => req("GET", `/tops/${tid}/anlagen`),
  uploadAnlage: (tid, file) => {
    const fd = new FormData();
    fd.append("datei", file);
    return fetch(`/api${"/tops/" + tid + "/anlagen"}`, {
      method: "POST", body: fd,
      headers: { Authorization: "Bearer " + (localStorage.getItem("internal_token") || "") },
    }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
  },
  deleteAnlage: (aid) => req("DELETE", `/anlagen/${aid}`),
  anlageDateiUrl: (aid) => `/api/anlagen/${aid}/datei.pdf`,
};
